export interface NormalizedGraphqlError {
	extensions?: unknown;
	message: string;
	path?: unknown;
}

export interface NormalizedApiError {
	endpoint: string | null;
	graphqlErrors: NormalizedGraphqlError[] | null;
	message: string;
	raw: unknown;
	status: number | string | null;
}

const FALLBACK_MESSAGE = "Unknown API error";

const isRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === "object" && value !== null && !Array.isArray(value);

const extractGraphqlErrors = (
	payload: Record<string, unknown>,
	endpointName: string | null
): NormalizedGraphqlError[] | null => {
	const candidates: unknown[] = [
		payload.errors,
		isRecord(payload.data) ? payload.data.errors : undefined,
		isRecord(payload.response) ? payload.response.errors : undefined,
		endpointName && isRecord(payload[endpointName])
			? (payload[endpointName] as Record<string, unknown>).errors
			: undefined
	];

	for (const candidate of candidates) {
		if (Array.isArray(candidate) && candidate.length > 0) {
			return candidate.map((entry) => {
				if (!isRecord(entry)) return { message: String(entry) };
				return {
					extensions: entry.extensions,
					message: typeof entry.message === "string" ? entry.message : "GraphQL error",
					path: entry.path
				};
			});
		}
	}
	return null;
};

const extractTopLevelMessage = (payload: Record<string, unknown>): string | null => {
	if (typeof payload.message === "string" && payload.message.length > 0) return payload.message;
	if (isRecord(payload.data) && typeof payload.data.message === "string" && payload.data.message.length > 0) {
		return payload.data.message;
	}
	if (typeof payload.error === "string" && payload.error.length > 0) return payload.error;
	if (isRecord(payload.error) && typeof payload.error.message === "string" && payload.error.message.length > 0) {
		return payload.error.message;
	}
	return null;
};

const normalizeApiError = (payload: unknown, endpointName: string | null = null): NormalizedApiError => {
	const base: NormalizedApiError = {
		endpoint: endpointName,
		graphqlErrors: null,
		message: FALLBACK_MESSAGE,
		raw: payload,
		status: null
	};

	if (payload == null) return base;
	if (typeof payload === "string") return { ...base, message: payload };
	if (typeof payload !== "object") return { ...base, message: String(payload) };

	const obj = payload as Record<string, unknown>;
	const status = obj.status;
	const graphqlErrors = extractGraphqlErrors(obj, endpointName);
	const topLevelMessage = extractTopLevelMessage(obj);
	const firstGraphqlMessage = graphqlErrors?.[0]?.message ?? null;

	return {
		endpoint: endpointName,
		graphqlErrors,
		message: topLevelMessage ?? firstGraphqlMessage ?? FALLBACK_MESSAGE,
		raw: payload,
		status: typeof status === "number" || typeof status === "string" ? status : null
	};
};

export const formatApiErrorHeadline = (normalized: NormalizedApiError): string => {
	const endpoint = normalized.endpoint ?? "Unknown";
	const status = normalized.status != null ? ` ${normalized.status}` : "";
	return `[${endpoint}${status}] ${normalized.message}`;
};

export default normalizeApiError;
